import { Sort } from "icons/font-awesome/light"
import { SortDown, SortUp } from "icons/font-awesome/solid"
import { useTranslations } from "modules/translations/index"
import React, { FormEvent, useState } from "react"
import { BaseTableProps, KeyProperties, renderTable, TableColumn } from "ui-kit/lists/table"

export type PossibleInteractiveColumns<T> = { [P in keyof T]: InteractiveTableColumn<T, P> }[keyof T]

export interface InteractiveTableProps<T> extends BaseTableProps<T> {
    /**
     * If a search field should be shown above the table to filter its contents.
     */
    searchable?: boolean

    columns: Array<PossibleInteractiveColumns<T>>

    /**
     * The table is initially sorted with this column, by default the first column.
     */
    defaultSortColumn?: KeyProperties<T>
}

export interface InteractiveTableColumn<T, P extends keyof T> extends TableColumn<T, P> {
    /**
     * If this column can be sorted. Default true.
     */
    sortable?: boolean

    /**
     * If this column can be searched. Default true.
     */
    searchable?: boolean

    /**
     * How this column should be sorted ascendingly. By default the column will
     * be sorted using `string.localeCompare()`.
     */
    sortFunction?: ( a: T[P], b: T[P] ) => number

    /**
     * If this column should be sorted ascending by default (when first clicked).
     * Default true.
     */
    defaultAscending?: boolean
}

function InteractiveTable<T> ( props: InteractiveTableProps<T> ): JSX.Element {
    const [searchTerm, setSearchTerm] = useState( "" )
    const [sortColumn, setSortColumn] = useState(
        props.columns.find( column => column.property === props.defaultSortColumn )
            ?? props.columns.find( column => column.sortable ?? true )
            ?? props.columns[0]
    )
    const [sortAscending, setSortAscending] = useState( sortColumn.defaultAscending ?? true )
    const { translate } = useTranslations()
    const searchInput = React.createRef<HTMLInputElement>()
    const searchable = props.searchable ?? true

    const sortedAndFilteredData =
        sortData( filterData( props.data, searchTerm, props ), sortColumn, sortAscending )

    function handleSearchSubmit ( event: FormEvent ): void {
        setSearchTerm( searchInput.current!.value! )
        event.preventDefault()
    }

    function sortByColumn ( column: PossibleInteractiveColumns<T> ): void {
        if ( column.header !== sortColumn.header ) {
            setSortColumn( column )
            setSortAscending( column.defaultAscending ?? true )
        } else {
            setSortAscending( !sortAscending )
        }
    }

    return <div className={props.className}>
        {searchable && <form onSubmit={handleSearchSubmit}>
            <label htmlFor="tableSearch" className="sr-only">
                {translate( "ui-kit.lists.interactive-table.search" )}
            </label>
            <div className="mb-2 relative rounded-md shadow-sm max-w-xs">
                <input id="tableSearch" name="searchterm" ref={searchInput} type="search"
                    onChange={( event ) => setSearchTerm( event.target.value )}
                    placeholder={translate( "ui-kit.lists.interactive-table.search" )}
                    className="form-input block w-full sm:text-sm sm:leading-5" />
            </div>
        </form>}
        <div className={`-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 ${props.className || ""}`}>
            {renderTable(
                { ...props, data: sortedAndFilteredData, className: undefined },
                createHeaderMapper( sortColumn, sortAscending, sortByColumn, translate )
            )}
        </div>
    </div>
}

function filterData<T> ( data: T[], searchTerm: string, props: InteractiveTableProps<T> ): T[] {
    if ( !searchTerm ) {
        return data
    }
    const lowercaseSearch = searchTerm.toLocaleLowerCase()
    return props.data.filter( value =>
        props.columns.some( column => {
            if ( !( column.searchable ?? true ) ) {
                return false
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-assignment
            let colValue = value[column.property] as any
            if ( column.transform ) {
                colValue = column.transform( colValue, value )
            }
            return String( colValue ).toLocaleLowerCase().includes( lowercaseSearch )
        } )
    )
}

function sortData<T> ( data: T[], sortColumn: PossibleInteractiveColumns<T>, ascending: boolean ): T[] {
    const ascendingMultiplier = ascending ? 1 : -1
    return data.concat( [] ).sort( ( a, b ) => {
        if ( !sortColumn.sortFunction ) {
            const aValue = sortColumn.transform ? sortColumn.transform( a[sortColumn.property], a ) : a[sortColumn.property]
            const bValue = sortColumn.transform ? sortColumn.transform( b[sortColumn.property], b ) : b[sortColumn.property]
            return String( aValue ).localeCompare( String( bValue ) ) * ascendingMultiplier
        }
        return sortColumn.sortFunction.call( null, a[sortColumn.property], b[sortColumn.property] ) * ascendingMultiplier
    } )
}

function createHeaderMapper<T> (
    sortColumn: PossibleInteractiveColumns<T>,
    sortAscending: boolean,
    setSortColumn: ( column: PossibleInteractiveColumns<T> ) => void,
    translate: ( key: string ) => string
): ( props: InteractiveTableProps<T> ) => JSX.Element[] {
    return function ( props: InteractiveTableProps<T> ): JSX.Element[] {
        return props.columns.map( column => {
            const headerClasses = "pl-6 pr-5 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider whitespace-no-wrap"
            let columnSortIcon = <Sort />
            let sortText = ""
            if ( column.header === sortColumn.header ) {
                columnSortIcon = sortAscending ? <SortDown /> : <SortUp />
                sortText = sortAscending
                    ? translate( "ui-kit.lists.interactive-table.sorted-ascending" )
                    : translate( "ui-kit.lists.interactive-table.sorted-descending" )
            }

            if ( column.sortable ?? true ) {
                return <th key={column.header} className="p-0">
                    <button type="button" onClick={() => setSortColumn( column )}
                        className={`${headerClasses} group w-full flex hover:bg-brand-100 focus:bg-brand-100 hover:text-gray-600 focus:text-gray-600`}>
                        <span className="flex-1">{column.header}</span>
                        <div className="text-base text-gray-400 group-hover:text-gray-500">
                            <span className="sr-only">{sortText}</span>
                            {columnSortIcon}
                        </div>
                    </button>
                </th>
            } else {
                return <th key={column.header} className={headerClasses}>
                    {column.header}
                </th>
            }
        } )
    }
}

export default InteractiveTable
